import React from 'react';
import { format as dateFormat } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  TableContainer,
  Table,
  Thead,
  Tbody,
  TablePagination,
  TableCell,
  SortIcon
} from './StyledTable';
interface Column {
  id: string;
  label: string;
  type: string;
  width?: string;
  sortable?: boolean;
  format?: string;
  align?: 'left' | 'center' | 'right';
}

interface DataTableProps {
  columns: Column[];
  data: Record<string, any>[];
  metadata?: {
    totalRows: number;
    currentPage: number;
    totalPages: number;
    pageSize: number;
  };
  onSort?: (columnId: string) => void;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  error?: string;
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  metadata,
  onSort,
  onPageChange,
  loading,
  error
}) => {
  const { t } = useTranslation();
  // Format cell value based on column type
  const formatCellValue = (value: any, column: Column) => {
    if (value === null || value === undefined) return '-';

    switch (column.type) {
      case 'date':
        return column.format 
          ? dateFormat(new Date(value), column.format)
          : new Date(value).toLocaleDateString();
      case 'boolean':
        return value ? '✓' : '✗';
      case 'number':
        return typeof value === 'number' 
          ? value.toLocaleString()
          : value;
      default:
        return value;
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <TableContainer>
      <Table>
        <Thead>
          <tr>
            {columns.map((column) => (
              <th 
                key={column.id}
                style={{ 
                  width: column.width,
                  textAlign: column.align || 'left',
                  cursor: column.sortable ? 'pointer' : 'default'
                }}
                onClick={() => column.sortable && onSort?.(column.id)}
              >
                {column.label}
                {column.sortable && <SortIcon />}
              </th>
            ))}
          </tr>
        </Thead>
        <Tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column) => (
                <TableCell 
                  key={column.id}
                  align={column.align}
                >
                  {formatCellValue(row[column.id], column)}
                </TableCell>
              ))}
            </tr>
          ))}
        </Tbody>
      </Table>
      
      {metadata && metadata.totalPages > 1 && (
        <TablePagination>
          <button
            disabled={metadata.currentPage === 1}
            onClick={() => onPageChange?.(metadata.currentPage - 1)}
          >
            {t('previous')}
          </button>
          <span>
            {`${t('page')} ${metadata.currentPage} ${t('of')} ${metadata.totalPages} (${metadata.totalRows} ${t('totalRows')})`}
          </span>
          <button
            disabled={metadata.currentPage === metadata.totalPages}
            onClick={() => onPageChange?.(metadata.currentPage + 1)}
          >
            {t('next')}
          </button>
        </TablePagination>
      )}
    </TableContainer>
  );
};